import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { api, type User, type UpdateUserPayload, type CreateUserPayload } from '../../utils/api';

export type RequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface UsersListState {
  data: User[];
  status: RequestStatus;
  error: string | null;
}

interface UserDetailState {
  data: User | null;
  status: RequestStatus;
  error: string | null;
}

interface UsersState {
  list: UsersListState;
  detail: UserDetailState;
}

const initialState: UsersState = {
  list: {
    data: [],
    status: 'idle',
    error: null,
  },
  detail: {
    data: null,
    status: 'idle',
    error: null,
  },
};

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const responseMessage =
      typeof error.response?.data === 'object'
        ? (error.response?.data as { message?: string })?.message
        : undefined;
    return responseMessage ?? error.message ?? 'Something went wrong.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
};

export const fetchUsersThunk = createAsyncThunk<User[], void, { rejectValue: string }>(
  'users/fetchList',
  async (_, thunkAPI) => {
    try {
      const response = await api.get<User[]>('/users');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const fetchUserByIdThunk = createAsyncThunk<User, string, { rejectValue: string }>(
  'users/fetchById',
  async (userId, thunkAPI) => {
    try {
      const response = await api.get<User>(`/users/${userId}`);
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const updateUserThunk = createAsyncThunk<User, { userId: string; payload: UpdateUserPayload }, { rejectValue: string }>(
  'users/update',
  async ({ userId, payload }, thunkAPI) => {
    try {
      const response = await api.put<User>(`/users/${userId}`, payload);
      toast.success('User updated successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deleteUserThunk = createAsyncThunk<string, string, { rejectValue: string }>(
  'users/delete',
  async (userId, thunkAPI) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully.');
      return userId;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createUserThunk = createAsyncThunk<User, CreateUserPayload, { rejectValue: string }>(
  'users/create',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post<User>('/users', payload);
      toast.success('User created successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUsersFromCache(state, action: PayloadAction<User[]>) {
      state.list.data = action.payload;
      state.list.status = 'succeeded';
      state.list.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchUsersThunk.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchUsersThunk.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.data = action.payload;
      })
      .addCase(fetchUsersThunk.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = action.payload ?? 'Unable to load users.';
      })
      // Fetch by id
      .addCase(fetchUserByIdThunk.pending, (state) => {
        state.detail.status = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchUserByIdThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((user) => user._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(fetchUserByIdThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to load user.';
      })
      // Update user
      .addCase(updateUserThunk.pending, (state) => {
        state.detail.status = 'loading';
        state.detail.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((user) => user._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to update user.';
      })
      // Delete user
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter((user) => user._id !== action.payload);
        if (state.detail.data?._id === action.payload) {
          state.detail.data = null;
          state.detail.status = 'idle';
        }
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.detail.error = action.payload ?? 'Unable to delete user.';
      })
      // Create user
      .addCase(createUserThunk.fulfilled, (state, action) => {
        state.list.data = [action.payload, ...state.list.data];
        state.list.status = 'succeeded';
      })
      .addCase(createUserThunk.rejected, (state, action) => {
        state.list.error = action.payload ?? 'Unable to create user.';
      });
  },
});

export const { setUsersFromCache } = usersSlice.actions;
export default usersSlice.reducer;
